import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import seo from '../seo/seoConfig.json';
import TitleText from '../components/common/TitleText';

const { business } = seo;
const EMAIL = 'proomtb@gmail.com';
const UPDATED = '8 de octubre de 2026';

const Mail = () => <a href={`mailto:${EMAIL}`} className="underline hover:text-black">{EMAIL}</a>;

const Section = ({ title, children }) => (
    <section className="mt-8">
        <h2 className="text-lg font-bold text-gray-900 mb-3">{title}</h2>
        <div className="space-y-3">{children}</div>
    </section>
);

const List = ({ items }) => (
    <ul className="list-disc pl-5 space-y-1.5">
        {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
);

const Privacy = () => (
    <>
        <p>En PROO MTB &amp; ROAD ("PROOMTB") cuidamos los datos que nos compartes en proomtb.com. Aquí explicamos qué datos recogemos, para qué los usamos y cómo puedes pedir que los cambiemos o borremos.</p>

        <Section title="1. Quiénes somos">
            <p>PROO MTB SRL, operando como PROO MTB &amp; ROAD, {business.streetAddress}, {business.addressLocality}, República Dominicana. Teléfono: {business.telephoneDisplay}. Correo: <Mail />.</p>
        </Section>

        <Section title="2. Qué datos recogemos">
            <List items={[
                <><strong>Cuenta en la página:</strong> correo electrónico y, si entras con Google, el nombre y la foto de tu cuenta de Google.</>,
                <><strong>Citas de taller:</strong> nombre completo, correo, teléfono, cédula, dirección, fecha de la cita y notas sobre tu bicicleta.</>,
                <><strong>Registro a eventos y paseos:</strong> nombre, cédula, teléfono, correo, talla de jersey, foto del comprobante de transferencia (cuando el evento tiene costo) y tu aceptación del descargo de responsabilidad.</>,
                <><strong>Confirmación de asistencia:</strong> nombre, correo, teléfono y cantidad de acompañantes.</>,
                <><strong>Fotos</strong> que subes a la página en actividades y eventos.</>,
                <><strong>Datos de navegación:</strong> páginas visitadas, tipo de dispositivo y ciudad aproximada, a través de Google Analytics. Ver la <Link to="/cookies" className="underline hover:text-black">Política de cookies</Link>.</>
            ]} />
        </Section>

        <Section title="3. Para qué los usamos">
            <List items={[
                'Agendar y atender tus citas de taller.',
                'Gestionar tu inscripción a eventos, confirmar pagos y entregar kits.',
                'Enviarte correos sobre los eventos en los que te inscribiste: confirmaciones, recordatorios e invitaciones.',
                'Responder tus consultas por WhatsApp, teléfono o correo.',
                'Entender cómo se usa la página para mejorarla.'
            ]} />
            <p>No vendemos ni alquilamos tus datos.</p>
        </Section>

        <Section title="4. Con quién los compartimos">
            <p>Solo con los proveedores que necesitamos para operar la página, y solo para ese fin:</p>
            <List items={[
                <><strong>Supabase:</strong> base de datos y almacenamiento de archivos.</>,
                <><strong>Resend:</strong> envío de correos.</>,
                <><strong>Google:</strong> estadísticas de visitas (Google Analytics) e inicio de sesión con Google.</>,
                <><strong>Hostinger:</strong> alojamiento de la página.</>
            ]} />
            <p>Algunos de estos proveedores guardan los datos fuera de República Dominicana. También podemos entregar datos si una autoridad competente lo exige por ley.</p>
        </Section>

        <Section title="5. Cuánto tiempo los guardamos">
            <p>Mientras tengas una cuenta, una cita o una inscripción activa, y luego hasta 2 años, salvo que la ley exija guardarlos por más tiempo.</p>
        </Section>

        <Section title="6. Tus derechos">
            <p>De acuerdo con la Ley No. 172-13 sobre protección de datos personales, puedes pedir acceso a tus datos, corregirlos, borrarlos u oponerte a su uso. Escríbenos a <Mail /> y te respondemos en un plazo de 10 días hábiles.</p>
        </Section>

        <Section title="7. Seguridad">
            <p>Usamos conexión segura (HTTPS) y acceso restringido a los datos: solo el personal autorizado de PROOMTB puede verlos.</p>
        </Section>

        <Section title="8. Menores de edad">
            <p>Si eres menor de 18 años, regístrate o agenda una cita con la autorización de tu madre, padre o tutor.</p>
        </Section>

        <Section title="9. Cambios">
            <p>Si cambiamos esta política, publicaremos la nueva versión en esta página con su fecha.</p>
        </Section>
    </>
);

const Cookies = () => (
    <>
        <p>Las cookies son pequeños archivos que la página guarda en tu navegador. En proomtb.com usamos muy pocas, y estas son:</p>

        <Section title="1. Necesarias para que la página funcione">
            <List items={[
                <><strong>Inicio de sesión:</strong> mantiene tu sesión abierta mientras navegas (Supabase).</>,
                <><strong>Carrito:</strong> recuerda los productos que agregaste.</>
            ]} />
            <p>Se guardan en tu navegador y no se usan para publicidad. Sin ellas no podrías iniciar sesión ni usar el carrito.</p>
        </Section>

        <Section title="2. Estadísticas">
            <p><strong>Google Analytics y Google Tag Manager:</strong> nos dicen cuántas personas visitan la página, qué páginas ven y desde qué tipo de dispositivo. Google guarda cookies como _ga y _ga_* por hasta 2 años.</p>
            <p>Más información: <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer" className="underline hover:text-black">policies.google.com/technologies/cookies</a></p>
        </Section>

        <Section title="3. Cómo desactivarlas">
            <p>Puedes borrar o bloquear las cookies desde la configuración de tu navegador. Para no ser medido por Google Analytics en ningún sitio, instala el complemento oficial de Google: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="underline hover:text-black">tools.google.com/dlpage/gaoptout</a></p>
            <p>Si bloqueas las cookies necesarias, algunas funciones, como iniciar sesión o el carrito, pueden dejar de funcionar.</p>
        </Section>

        <Section title="4. Contacto">
            <p>Si tienes preguntas, escríbenos a <Mail /> o llámanos al {business.telephoneDisplay}. También puedes leer nuestra <Link to="/privacidad" className="underline hover:text-black">Política de privacidad</Link>.</p>
        </Section>
    </>
);

const PAGES = {
    privacidad: { title: 'Política de privacidad', Body: Privacy },
    cookies: { title: 'Política de cookies', Body: Cookies }
};

const LegalPage = ({ page }) => {
    const { title, Body } = PAGES[page];
    return (
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-gray-700 leading-relaxed">
            <Helmet>
                <title>{`${title} | PROOMTB & ROAD`}</title>
            </Helmet>
            <h1 className="text-3xl sm:text-4xl font-black italic text-gray-900 tracking-tight"><TitleText text={title} dark /></h1>
            <p className="mt-2 mb-8 text-sm text-gray-500">Última actualización: {UPDATED}</p>
            <Body />
        </main>
    );
};

export default LegalPage;
